import { authenticatedInstance } from '../index/index.service';
import type { IdResponse, ProductImportPreview, ProductVariantsCombosView, ProductView as SharedProductView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { ProductVariantsCombosView } from 'shared-models/browser';
export type { ProductImportPreview, ProductImportRowResult, ProductImportIssue } from 'shared-models/browser';

export type ProductView = SharedProductView & {
  kds: boolean;
  limit: number | null;
};

export type UpsertProductRequest = {
  name: string;
  price: number;
  printerIds: number[];
  limit?: number | null;
  kds?: boolean;
  categoryId?: number | null;
};

export type UpdateProductRequest = {
  name?: string;
  price?: number;
  printerIds?: number[];
  limit?: number | null;
  kds?: boolean;
  categoryId?: number | null;
  woltMenuIncluded?: boolean;
};

export type BulkUpdateProductsRequest = {
  ids: number[];
  categoryId?: number | null;
  price?: number;
  printerIds?: number[];
  kds?: boolean;
};

class ProductsService {
  // Architectural summary: Loads tenant products and printer routing mapping for manager product settings screens.
  async listForSettings(tenantId: string, employeeSession: string): Promise<ProductView[]> {
    try {
      const response = await authenticatedInstance.get<ProductView[]>(`/products/tenant/${tenantId}`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Loads active POS-visible products (any employee role) for the order screen product grid.
  async listForPos(tenantId: string, employeeSession: string): Promise<ProductView[]> {
    try {
      const response = await authenticatedInstance.get<ProductView[]>(`/products/pos/tenant/${tenantId}`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Returns a product's variant/combo composition for the POS menu tap (drives the items modal).
  async getComposition(tenantId: string, productId: number, employeeSession: string): Promise<ProductVariantsCombosView> {
    try {
      const response = await authenticatedInstance.get<ProductVariantsCombosView>(
        `/products/tenant/${tenantId}/${productId}/composition`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Downloads the tenant catalog as an .xlsx blob (manager export).
  async exportXlsx(tenantId: string, employeeSession: string): Promise<Blob> {
    try {
      const response = await authenticatedInstance.get(`/products/tenant/${tenantId}/export`, {
        headers: { 'x-employee-session': employeeSession },
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Upload .xlsx to import products — preview, or apply when commit=true.
  async importXlsx(tenantId: string, file: File, employeeSession: string, commit: boolean): Promise<ProductImportPreview> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authenticatedInstance.post<ProductImportPreview>(
        `/products/tenant/${tenantId}/import${commit ? '?commit=true' : ''}`,
        formData,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Creates tenant product rows with assigned printer ids from manager settings.
  async create(tenantId: string, payload: UpsertProductRequest, employeeSession: string): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>(`/products/tenant/${tenantId}`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Updates tenant product metadata and assigned printer ids from manager settings edit flow.
  async update(productId: number, payload: UpdateProductRequest, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/products/${productId}`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Uploads a manager-selected product image through the
  // backend normalization/storage pipeline.
  async uploadImage(productId: number, imageFile: File, employeeSession: string): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      await authenticatedInstance.post<void>(
        `/products/${productId}/image`,
        formData,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Batch-sets one limit (null/0..99) on many products in a single
  async updateLimits(productIds: number[], limit: number | null, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        '/products/limits',
        { ids: productIds, limit },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Bulk-applies a field subset to many products; rows arrive via SSE.
  async bulkUpdate(payload: BulkUpdateProductsRequest, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>('/products/bulk', payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Drag-drop reorder — sends the new product id order as one batch.
  async reorder(orderedIds: number[], employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        '/products/reorder',
        { orderedIds },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Deactivates a product from manager settings while preserving tenant-scoped employee-session authorization context.
  async deactivate(productId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/products/${productId}/deactivate`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Reactivates a product from manager settings while preserving tenant-scoped employee-session authorization context.
  async activate(productId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/products/${productId}/activate`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const productsService = new ProductsService();
